import React from 'react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Activity, Moon, Sun, ArrowLeft, Heart, Shield, Users,
  Target, Eye, Globe, CheckCircle, Brain, Video, Home,
  Stethoscope, UserCheck, Lock, Zap, Award, Sparkles
} from 'lucide-react';

export default function AboutPage() {
  const { theme, toggleTheme } = useTheme();

  const services = [
    { icon: Stethoscope, text: 'Skilled nursing care' },
    { icon: Heart, text: 'Hospice or palliative support' },
    { icon: Home, text: 'Post-hospital recovery assistance' },
    { icon: Activity, text: 'Chronic disease management' },
    { icon: Users, text: 'Companionship or personal care' },
    { icon: Video, text: 'Telehealth visits' }
  ];

  const missionPoints = [
    'Reduce the friction of finding trusted care',
    'Improve care outcomes through better alignment',
    'Increase transparency and safety in private healthcare services',
    'Empower clinicians with meaningful, well-matched engagements',
    'Create scalable, human-centered healthcare connections'
  ];

  const visionPoints = [
    'Families never struggle to find qualified, trustworthy care',
    'Clinicians are matched to cases that align with their expertise and capacity',
    'AI enhances — not replaces — compassionate healthcare delivery',
    'Telehealth and in-person care work seamlessly together',
    'Healthcare matching is proactive, predictive, and personalized'
  ];

  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Every in-person clinician undergoes identity verification, license validation, and background screening.'
    },
    {
      icon: Brain,
      title: 'Intelligent Matching',
      description: 'AI evaluates both patient needs and clinician expertise to ensure the right match — not just the next available one.'
    },
    {
      icon: Lock,
      title: 'Privacy Protected',
      description: 'Telehealth visits occur through secure, encrypted technology designed to protect patient privacy.'
    },
    {
      icon: Heart,
      title: 'Compassion Driven',
      description: 'We\'re building where safety, intelligence, and compassion intersect for personalized healthcare.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-cn-bone-100 via-white to-cn-bone-200 dark:from-cn-ink-950 dark:via-cn-ink-900 dark:to-cn-ink-950" data-testid="about-page">
      <Seo
        title="About CaraLoom | How Our Home Care Marketplace Works"
        description="Learn how CaraLoom connects families with verified, background-checked nurses and caregivers for in-home care — AI-matched, transparent, and agency-free. Serving families nationwide."
        path="/about"
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }])]}
      />
      <PublicNavbar />

      {/* Hero Section */}
      <section className="pt-12 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-cn-primary dark:text-blue-400 hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-center mb-12">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cn-sage-100 rounded-full border border-cn-primary/15 mb-6">
                <Sparkles className="w-4 h-4 text-cn-primary" />
                <span className="text-sm font-medium text-cn-primary">About CaraLoom</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white font-display mb-6 leading-tight">
                Finding the right care
                <span className="block italic font-medium text-cn-primary">shouldn't feel overwhelming.</span>
              </h1>
              <p className="text-lg text-cn-text-muted leading-relaxed max-w-xl">
                We're building the world's most trusted home-healthcare marketplace — gently
                connecting families with the right clinician, every time.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl overflow-hidden cn-card aspect-[3/4]">
                  <img src="https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRleHR1cmUlMjB3aGl0ZSUyMGdyZXl8ZW58MHx8fHwxNzgxMDY5NjE1fDA&ixlib=rb-4.1.0&q=85" alt="Abstract clinical texture" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden cn-card aspect-[3/4] mt-8">
                  <img src="https://images.pexels.com/photos/7108116/pexels-photo-7108116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=720&w=540" alt="Precision instruments" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>

          {/* Main About Content */}
          <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-xl mb-12">
            <CardContent className="p-8 lg:p-12">
              <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-8">
                At CaraLoom, we believe finding the right healthcare professional shouldn't feel overwhelming. We are a technology-driven healthcare matchmaking platform designed to intelligently connect families and patients with licensed, vetted clinicians — based on real care needs, clinical complexity, availability, and compatibility.
              </p>

              <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-8">
                CaraLoom combines advanced artificial intelligence with rigorous credential verification to create safe, thoughtful, and personalized care pairings. Whether you are seeking:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-cn-ink-700/50">
                    <div className="w-10 h-10 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-lg flex items-center justify-center">
                      <service.icon className="w-5 h-5 text-cn-primary dark:text-blue-400" />
                    </div>
                    <span className="text-slate-700 dark:text-slate-300 font-medium">{service.text}</span>
                  </div>
                ))}
              </div>

              <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-8">
                Our platform evaluates both the patient's needs and the clinician's expertise to ensure the right match — not just the next available one.
              </p>

              <div className="bg-gradient-to-r from-cn-primary to-cn-sage-500 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 mb-8">
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                  Every in-person clinician on our platform undergoes <strong>identity verification</strong>, <strong>license validation</strong>, and <strong>background screening</strong> when applicable. Telehealth visits occur through secure, encrypted technology designed to protect patient privacy.
                </p>
              </div>

              <p className="text-xl text-center font-semibold text-slate-900 dark:text-white">
                We are building the future of personalized healthcare access — where safety, intelligence, and compassion intersect.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-cn-ink-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-gradient-to-b from-slate-50 to-white dark:from-cn-ink-800 dark:to-cn-ink-800/50 border border-slate-100 dark:border-cn-ink-700">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-cn-primary dark:text-blue-400" />
                  </div>
                  <h3 className="font-bold text-lg text-slate-900 dark:text-white font-display mb-2">{value.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-sky-50 to-white dark:from-cn-ink-800/50 dark:to-cn-ink-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cn-sage-50 dark:bg-blue-900/30 rounded-full border border-blue-100 dark:border-blue-800 mb-6">
                <Target className="w-4 h-4 text-cn-primary dark:text-blue-400" />
                <span className="text-sm font-medium text-cn-primary dark:text-blue-400">Our Mission</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white font-display mb-6">
                Simplifying Access to Quality Healthcare
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
                To simplify access to high-quality healthcare by using artificial intelligence to match patients and families with the right licensed clinicians — safely, intelligently, and efficiently.
              </p>
              <p className="text-slate-700 dark:text-slate-300 font-medium mb-4">We exist to:</p>
              <ul className="space-y-3">
                {missionPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-slate-400">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-3xl blur-3xl" />
              <Card className="relative bg-gradient-to-br from-cn-primary to-cn-sage-500 border-0 text-white">
                <CardContent className="p-8">
                  <Award className="w-12 h-12 text-white/80 mb-4" />
                  <h3 className="text-2xl font-bold font-display mb-4">Our Commitment</h3>
                  <p className="text-cn-sage-100 leading-relaxed">
                    Every match we make is backed by verified credentials, AI-powered compatibility analysis, and a commitment to putting patient safety first.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-cn-ink-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/30 rounded-full border border-purple-100 dark:border-purple-800 mb-6">
            <Globe className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Our Vision</span>
          </div>
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white font-display mb-6">
            A World of Better Healthcare Access
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-12">
            We envision a world where:
          </p>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            {visionPoints.map((point, index) => (
              <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 dark:bg-cn-ink-800">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/50 dark:to-pink-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-slate-700 dark:text-slate-300">{point}</span>
              </div>
            ))}
          </div>
          <p className="text-xl font-semibold text-slate-900 dark:text-white mt-12">
            CaraLoom aims to become the most trusted intelligent healthcare pairing network in the country.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cn-primary to-cn-sage-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white font-display mb-4">
            Ready to Experience Better Care Matching?
          </h2>
          <p className="text-lg text-cn-sage-100 mb-8">
            Join the growing community of families and clinicians on CaraLoom
          </p>
          <Link to="/auth">
            <Button className="bg-white text-cn-primary hover:bg-cn-sage-50 rounded-full px-10 h-14 text-lg font-medium shadow-lg">
              Get Started Today
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-900">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500 dark:text-slate-400">
          <p>© {new Date().getFullYear()} CaraLoom. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
